import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormGroup, Grid, Typography } from '@mui/material'
import omit from 'lodash/omit'
import { CatalogProductType } from '../enums'
import { CatalogProduct } from '../types'
import CatalogProductForm, {
  CatalogProductSelectField,
  ElectricalCabinetFields,
  SpineConfigFields
} from './CatalogProductForm'
import { ElectricalCabinetCatalogProduct, ValveCatalogProduct } from '../types/CatalogProduct'
import { ValveCatalogProductCustomDataInput } from '../types/CatalogProductInput'

export interface ValveCatalogProductFormProps {
  catalogProduct?: ValveCatalogProduct
  onCancel?: () => void
  onError?: (error: unknown) => void
  afterSubmit?: (catalogProduct: CatalogProduct) => void
}

export default function ValveCatalogProductForm({
  catalogProduct,
  ...props
}: ValveCatalogProductFormProps) {
  return (
    <CatalogProductForm
      type={CatalogProductType.VALVE}
      catalogProduct={catalogProduct}
      {...props}
    />
  )
}

export interface ValveCatalogProductCustomDataFieldsProps {
  value: ValveCatalogProductCustomDataInput
  catalogProduct?: CatalogProduct
  disabled?: boolean
  isError?: (field: string) => boolean
  getHelperText?: (field: string) => string | undefined
  onChange: (value: ValveCatalogProductCustomDataInput) => void
}

export function ValveCatalogProductCustomDataFields(
  props: ValveCatalogProductCustomDataFieldsProps
) {
  const [electricalCabinet, setElectricalCabinet] =
    useState<ElectricalCabinetCatalogProduct | null>(null)

  return (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <SpineConfigFields {...props} electricalCabinet={electricalCabinet} />
      </Grid>

      <Grid item xs={12}>
        <ElectricalCabinetFields {...props} onElectricalCabinetChange={setElectricalCabinet} />
      </Grid>

      <Grid item xs={12}>
        <HeaterFields {...props} />
      </Grid>

      <Grid item xs={12}>
        <InRuuviTagFields {...props} />
      </Grid>

      <Grid item xs={12}>
        <OutRuuviTagFields {...props} />
      </Grid>
    </Grid>
  )
}

function HeaterFields({
  value,
  disabled = false,
  onChange
}: ValveCatalogProductCustomDataFieldsProps) {
  const { t } = useTranslation('catalogProduct')

  function handleHeaterChange(selected: CatalogProduct | null) {
    // The loaded catalog product is already linked.
    if (selected !== null && selected.id === value.heater?.catalogProductId) {
      return
    }

    if (selected === null) {
      onChange(omit(value, 'heater'))
      return
    }

    onChange({ ...value, heater: { catalogProductId: selected.id } })
  }

  return (
    <FormGroup>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('heater')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <CatalogProductSelectField
            type={CatalogProductType.HEATER}
            value={value.heater?.catalogProductId ?? null}
            disabled={disabled}
            onChange={handleHeaterChange}
          />
        </Grid>
      </Grid>
    </FormGroup>
  )
}

function InRuuviTagFields({
  value,
  disabled = false,
  onChange
}: ValveCatalogProductCustomDataFieldsProps) {
  const { t } = useTranslation('catalogProduct')

  function handleInRuuviTagChange(selected: CatalogProduct | null) {
    // The loaded catalog product is already linked.
    if (selected !== null && selected.id === value.inRuuviTag?.catalogProductId) {
      return
    }

    if (selected === null) {
      onChange(omit(value, 'inRuuviTag'))
      return
    }

    onChange({ ...value, inRuuviTag: { catalogProductId: selected.id } })
  }

  return (
    <FormGroup>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('inRuuviTag')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <CatalogProductSelectField
            type={CatalogProductType.RUUVI_TAG}
            value={value.inRuuviTag?.catalogProductId ?? null}
            disabled={disabled}
            onChange={handleInRuuviTagChange}
          />
        </Grid>
      </Grid>
    </FormGroup>
  )
}

function OutRuuviTagFields({
  value,
  disabled = false,
  onChange
}: ValveCatalogProductCustomDataFieldsProps) {
  const { t } = useTranslation('catalogProduct')

  function handleOutRuuviTagChange(selected: CatalogProduct | null) {
    // The loaded catalog product is already linked.
    if (selected !== null && selected.id === value.outRuuviTag?.catalogProductId) {
      return
    }

    if (selected === null) {
      onChange(omit(value, 'outRuuviTag'))
      return
    }

    onChange({ ...value, outRuuviTag: { catalogProductId: selected.id } })
  }

  return (
    <FormGroup>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('outRuuviTag')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <CatalogProductSelectField
            type={CatalogProductType.RUUVI_TAG}
            value={value.outRuuviTag?.catalogProductId ?? null}
            disabled={disabled}
            onChange={handleOutRuuviTagChange}
          />
        </Grid>
      </Grid>
    </FormGroup>
  )
}
